import type { DocumentTextBox } from "./documentTextAnnotations";

/** Share of the font height above the baseline when pdf.js reports no ascent. */
const DEFAULT_ASCENT_RATIO = .8;
const TEXT_DIRECTION_RTL = "rtl";

interface PdfTextItem { str: string; width: number; transform: number[]; fontName: string; dir: string }
interface PdfTextStyle { fontFamily: string; vertical?: boolean; ascent?: number; descent?: number }
interface TextViewport { width: number; height: number; transform: number[]; convertToViewportPoint(x: number, y: number): number[] }

/** Measure glyph advances in the loaded PDF font; never divide a text run into equal-width characters. */
export function pdfTextBox(item: PdfTextItem, style: PdfTextStyle, viewport: TextViewport, context: CanvasRenderingContext2D): DocumentTextBox {
  const m = viewport.transform, t = item.transform;
  const [x, y] = viewport.convertToViewportPoint(t[4], t[5]);
  const angle = Math.atan2(m[1] * t[0] + m[3] * t[1], m[0] * t[0] + m[2] * t[1]);
  const height = Math.hypot(m[0] * t[2] + m[2] * t[3], m[1] * t[2] + m[3] * t[3]);
  context.font = `${height}px "${item.fontName}", ${style.fontFamily}`;
  const metrics = context.measureText(item.str);
  const ascent = metrics.actualBoundingBoxAscent || height * (style.ascent ?? DEFAULT_ASCENT_RATIO);
  const descent = Math.max(0, metrics.actualBoundingBoxDescent || 0);
  const rectangle = (from: number, to: number) => {
    const points = [[from, -ascent], [to, -ascent], [from, descent], [to, descent]].map(([u, v]) =>
      [x + u * Math.cos(angle) - v * Math.sin(angle), y + u * Math.sin(angle) + v * Math.cos(angle)]);
    const left = Math.max(0, Math.min(...points.map(point => point[0])) / viewport.width);
    const top = Math.max(0, Math.min(...points.map(point => point[1])) / viewport.height);
    return { x: left, y: top, width: Math.max(0, Math.min(1, Math.max(...points.map(point => point[0])) / viewport.width) - left),
      height: Math.max(0, Math.min(1, Math.max(...points.map(point => point[1])) / viewport.height) - top) };
  };
  const box = { text: item.str, ...rectangle(0, item.width) };
  // Vertical and right-to-left runs require their own shaping map; retain only whole-run locations.
  if (style.vertical || item.dir === TEXT_DIRECTION_RTL || !metrics.width) return box;
  const characters = [];
  let start = 0;
  for (const character of item.str) {
    const end = start + character.length;
    const from = context.measureText(item.str.slice(0, start)).width / metrics.width * item.width;
    const to = context.measureText(item.str.slice(0, end)).width / metrics.width * item.width;
    characters.push({ start, end, ...rectangle(from, to) });
    start = end;
  }
  return { ...box, characters };
}
